import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, yen, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Plus, Trash2, Truck, PackagePlus } from "lucide-react";

export default function Belanja() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [supOpen, setSupOpen] = useState(false);
  const [supForm, setSupForm] = useState({ name: "", phone: "" });

  const load = useCallback(async () => {
    const [p, s, h] = await Promise.all([
      api.get("/products", { params: { kind: "grocery" } }),
      api.get("/suppliers"),
      api.get("/purchases"),
    ]);
    setProducts(p.data);
    setSuppliers(s.data);
    setPurchases(h.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const addItem = (id) => {
    const p = products.find((x) => x.id === id);
    if (!p || items.some((i) => i.product_id === id)) return;
    setItems([
      ...items,
      { product_id: p.id, name: p.name, qty: 1, cost: p.cost || 0, unit: p.unit },
    ]);
    setPick("");
  };

  const total = items.reduce((s, i) => s + i.qty * i.cost, 0);

  const save = async () => {
    if (items.length === 0) return toast.error(t("purchase_empty"));
    const clean = items.map((i) => ({
      product_id: i.product_id,
      name: i.name,
      unit: i.unit,
      qty: Number(i.qty) || 0,
      cost: Number(i.cost) || 0,
    }));
    if (clean.some((i) => i.qty <= 0)) return toast.error("Jumlah harus lebih dari 0");
    setBusy(true);
    try {
      await api.post("/purchases", { supplier_name: supplier, items: clean, note: "" });
      setItems([]);
      await load();
      toast.success(t("saved"));
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan belanja"));
    } finally {
      setBusy(false);
    }
  };

  const saveSupplier = async () => {
    if (!supForm.name.trim()) return;
    await api.post("/suppliers", supForm);
    setSupForm({ name: "", phone: "" });
    setSupOpen(false);
    load();
    toast.success("Pemasok ditambahkan");
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold">{t("belanja_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("belanja_sub")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={supplier} onValueChange={setSupplier}>
              <SelectTrigger className="w-56" data-testid="supplier-select">
                <SelectValue placeholder={t("pick_supplier")} />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.name}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" data-testid="add-supplier-btn" onClick={() => setSupOpen(true)}>
              <Truck size={16} className="mr-1" /> {t("new_supplier")}
            </Button>
          </div>

          <Select value={pick} onValueChange={addItem}>
            <SelectTrigger data-testid="purchase-product-select">
              <SelectValue placeholder={t("add_purchase_item")} />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} (stok {p.stock} {p.unit})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="space-y-2">
            {items.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t("purchase_empty")}
              </p>
            )}
            {items.map((it, idx) => (
              <div
                key={it.product_id}
                data-testid={`purchase-item-${it.product_id}`}
                className="flex items-center gap-2 bg-gray-50 rounded-xl p-3"
              >
                <span className="flex-1 text-sm font-medium truncate">{it.name}</span>
                <Input
                  data-testid={`purchase-qty-${it.product_id}`}
                  inputMode="decimal"
                  value={it.qty}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9.]/g, "");
                    setItems(items.map((x, i) => (i === idx ? { ...x, qty: v } : x)));
                  }}
                  className="w-20 h-9 text-right num"
                />
                <span className="text-xs text-muted-foreground w-14">{it.unit}</span>
                <Input
                  data-testid={`purchase-cost-${it.product_id}`}
                  inputMode="numeric"
                  placeholder="Harga beli"
                  value={it.cost}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    setItems(items.map((x, i) => (i === idx ? { ...x, cost: v } : x)));
                  }}
                  className="w-28 h-9 text-right num"
                />
                <span className="num text-sm w-20 text-right">
                  {yen((Number(it.qty) || 0) * (Number(it.cost) || 0))}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-destructive"
                  data-testid={`purchase-remove-${it.product_id}`}
                  onClick={() => setItems(items.filter((_, i) => i !== idx))}
                >
                  <Trash2 size={15} />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <span className="font-display font-semibold">{t("purchase_total")}</span>
            <span className="num text-2xl font-semibold" data-testid="purchase-total">
              {yen(total)}
            </span>
          </div>
          <Button
            className="w-full h-12"
            data-testid="save-purchase-btn"
            disabled={busy}
            onClick={save}
          >
            <PackagePlus size={18} className="mr-2" /> {t("save_purchase")}
          </Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="font-display font-semibold mb-4">{t("purchase_history")}</p>
          <div className="space-y-3" data-testid="purchase-history">
            {purchases.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("no_data")}</p>
            )}
            {purchases.map((p) => (
              <div key={p.id} className="border border-gray-100 rounded-xl p-3">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{p.supplier_name || "Tanpa pemasok"}</span>
                  <span className="num">{yen(p.total)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {new Date(p.created_at).toLocaleString("id-ID")} · {p.items.length} {t("items")}
                </p>
                <button
                  data-testid={`delete-purchase-${p.id}`}
                  className="text-xs text-destructive mt-1"
                  onClick={async () => {
                    await api.delete(`/purchases/${p.id}`);
                    load();
                    toast.success("Belanja dibatalkan");
                  }}
                >
                  {t("cancel")}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={supOpen} onOpenChange={setSupOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">{t("new_supplier")}</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="supplier-name-input"
            placeholder="Nama pemasok"
            value={supForm.name}
            onChange={(e) => setSupForm({ ...supForm, name: e.target.value })}
          />
          <Input
            data-testid="supplier-phone-input"
            placeholder="No. telepon (opsional)"
            value={supForm.phone}
            onChange={(e) => setSupForm({ ...supForm, phone: e.target.value })}
          />
          <Button data-testid="save-supplier-btn" onClick={saveSupplier}>
            <Plus size={16} className="mr-1" /> {t("save")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
